import { forwardRef } from 'react';
import { makeStyles } from '@material-ui/styles';
import Label from '../Label';
import FieldError from '../FieldError';
import cn from 'clsx';
import maxLengthValidator from '../helpers/maxLengthValidator';

type InputProps = JSX.IntrinsicElements['input'] & {
    label?: string;
    required?: boolean;
    error?: string;
};

const Input = forwardRef<HTMLInputElement, InputProps>(
    (
        {
            type = 'text',
            className,
            label,
            required,
            error,
            maxLength,
            onChange = () => {},
            ...domProps
        },
        ref
    ) => {
        const classes = useStyles();
        const classNames = cn(classes.input, className);

        return (
            <label className={classes.container}>
                <input
                    type={type}
                    ref={ref}
                    className={classNames}
                    maxLength={maxLength}
                    onChange={maxLengthValidator(onChange)}
                    {...domProps}
                />
                <div className={classes.meta}>
                    {label && <Label label={label} required={required} />}
                    {error && <FieldError error={error} />}
                </div>
            </label>
        );
    }
);

Input.displayName = 'Input';

const useStyles = makeStyles(
    theme => ({
        container: {
            display: 'flex',
            flexDirection: 'column-reverse',
        },
        input: {
            color: theme.palette.greys[600],
            padding: '4px 8px',
            border: 'none',
            ...theme.typography.t61,
            backgroundColor: theme.palette.greys[200],
            transition: 'color 300ms ease-in-out',
            width: '100%',
            borderRadius: 6,
            margin: 0,

            '&:focus': {
                outline: 'none',
                boxShadow: `0 0 0 1px ${theme.palette.greys[400]}`,
            },

            '&:disabled': {
                color: theme.palette.greys[400],
                cursor: 'not-allowed',
            },

            '&:-webkit-autofill': {
                '-webkit-text-fill-color': theme.palette.greys[600],
                boxShadow: `0 0 0px 60px ${theme.palette.greys[200]} inset`,
                backgroundClip: 'content-box',
                animation: '$autofill',
                animationFillMode: 'both',
                '&:focus': {
                    '-webkit-text-fill-color': theme.palette.greys[600],
                    boxShadow: `0 0 0 1px ${theme.palette.greys[400]}, 0 0 0px 60px ${theme.palette.greys[200]} inset`,
                },
                '&:first-line': {
                    color: theme.palette.greys[600],
                    ...theme.typography.t61,
                },
            },
        },
        meta: {
            display: 'flex',
            alignItems: 'center',
            marginBottom: 4,
        },
        '@keyframes autofill': {
            '100%': {
                color: 'inherit',
            },
        },
    }),
    { classNamePrefix: 'field' }
);

export default Input;
